import { FileOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Progress, Space, Typography, Upload, message } from 'antd';
import dayjs from 'dayjs';
import updateLocale from 'dayjs/plugin/updateLocale';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { axiosWithCSRF } from '../../fetchHooks/axiosWithCSRF';
import { envApiUrl } from '../../fetchHooks/envApiUrl';
import { getFileSize } from '../../pages/MyDayPage/FileItem';
import { Files } from '../../pages/MyDayPage/Files';
import { shouldUseChunkedUpload, uploadLargeFile } from '../../utils/simpleChunkedUpload';
import { Images } from '../Images/Images';
import styles from './UploadFilesDragger.module.css';
import 'dayjs/locale/en-gb';
import type { TFunction } from 'i18next';
import type { MydayType } from '../../fetchHooks/useMyday';

// Extend and update the locale
dayjs.extend(updateLocale);
dayjs.updateLocale('en-gb', {
  weekStart: 1, // Set Monday as the first day of the week
});

export const formatTimeDuration = (s: number, t: TFunction) => {
  let seconds = Math.floor(s);
  if (s < 60) return seconds + ' ' + t('upload.seconds');
  const minutes = Math.floor(s / 60);
  seconds = seconds - minutes * 60;
  return minutes + ' ' + t('upload.minutes') + ' ' + t('upload.and') + ' ' + seconds + ' ' + t('upload.seconds');
};

type UploadFileType = {
  name: string;
  uid: string;
  size: number;
  originalSize: number;
  progress: number;
  estimated: number;
  speed: number;
  isCompressed: boolean;
};

const UploadFilesDragger = ({ myday }: { myday: MydayType }) => {
  const { t } = useTranslation();
  const [fileList, setFileList] = useState<Record<string, UploadFileType>>({});
  const queryClient = useQueryClient();

  // Helper function to format bytes per second
  const formatSpeed = (bytesPerSecond: number) => {
    if (bytesPerSecond < 1024) return `${bytesPerSecond.toFixed(0)} B/s`;
    if (bytesPerSecond < 1024 * 1024) return `${(bytesPerSecond / 1024).toFixed(1)} KB/s`;
    if (bytesPerSecond < 1024 * 1024 * 1024) return `${(bytesPerSecond / (1024 * 1024)).toFixed(1)} MB/s`;
    return `${(bytesPerSecond / (1024 * 1024 * 1024)).toFixed(1)} GB/s`;
  };

  const handleUpload = async ({ file }: { file: File }) => {
    const startTime = Date.now();
    let lastProgressTime = startTime;
    let lastProgressBytes = 0;

    const getFileObject = (
      progress: number,
      estimated: number,
      speed: number,
      originalSize: number,
      compressedSize: number,
    ) => {
      return {
        name: file.name,
        uid: (file as any).uid || file.name, // Use uid if available, fallback to name
        size: compressedSize || file.size,
        originalSize: originalSize,
        progress: Math.ceil(progress * 100),
        estimated: estimated || 0,
        speed: speed || 0,
        isCompressed: compressedSize && compressedSize < originalSize,
      };
    };

    try {
      // No compression - upload files as-is to preserve quality
      const originalSize = file.size;
      const processedFile = file; // Use original file without compression
      const compressedSize = originalSize;

      if (myday.id) {
        // Check if we should use chunked upload for large files (20MB+)
        if (shouldUseChunkedUpload(processedFile)) {
          console.log(`🚀 [LARGE FILE] Using chunked upload for large file: ${file.name}`);

          // Initialize progress bar immediately
          setFileList((prev) => {
            return {
              ...prev,
              [(file as any).uid || file.name]: getFileObject(0, 0, 0, originalSize, compressedSize),
            };
          });

          const uploadUrl = `${envApiUrl}/files/large-chunk`;

          const result = await uploadLargeFile(processedFile, uploadUrl, {
            headers: {
              mydayId: myday.id,
            },
            credentials: 'include', // Include session cookies for authentication
            onProgress: (uploadedBytes: number, _totalBytes: number, progress: number) => {
              const speed = (uploadedBytes - lastProgressBytes) / ((Date.now() - lastProgressTime) / 1000);
              lastProgressTime = Date.now();
              lastProgressBytes = uploadedBytes;

              setFileList((prev) => {
                return {
                  ...prev,
                  [(file as any).uid || file.name]: getFileObject(progress, 0, speed, originalSize, compressedSize),
                };
              });
            },
          });

          if (result.success) {
            message.success(t('upload.fileUploadedSuccessfullyChunked', { fileName: file.name }));
            console.log('Large file uploaded successfully, invalidating queries.');
            // Add small delay to ensure backend has processed the file
            setTimeout(() => {
              // Invalidate file-related queries to refresh the file list
              queryClient.invalidateQueries({ queryKey: ['useImages', myday.id] });
              queryClient.invalidateQueries({ queryKey: ['useFilesOnly', myday.id] });
              queryClient.invalidateQueries({ queryKey: ['useAllFiles', myday.id] });
              queryClient.invalidateQueries({ queryKey: ['useMydays'] });
            }, 500);
          } else {
            message.error(t('upload.chunkedUploadFailed', { error: result.error || t('upload.unknownError') }));
          }
        } else {
          // Use regular upload for smaller files
          console.log(`📤 [UPLOAD] Using regular upload for file: ${file.name}`);

          const formData = new FormData();
          formData.append('file', processedFile);

          axiosWithCSRF({
            method: 'POST',
            url: envApiUrl + '/files',
            data: formData,
            headers: {
              'content-type': 'multipart/form-data',
              mydayId: myday.id,
            },
            onUploadProgress: (progressEvent) => {
              const currentTime = Date.now();
              const currentBytes = progressEvent.loaded;
              const timeDiff = (currentTime - lastProgressTime) / 1000; // Convert to seconds
              const bytesDiff = currentBytes - lastProgressBytes;

              // Calculate speed (bytes per second)
              const speed = timeDiff > 0 ? bytesDiff / timeDiff : 0;

              // Update tracking variables
              lastProgressTime = currentTime;
              lastProgressBytes = currentBytes;

              setFileList((prev) => {
                return {
                  ...prev,
                  [(file as any).uid || file.name]: getFileObject(
                    progressEvent?.progress || 0,
                    progressEvent?.estimated || (0 as number),
                    speed as number,
                    originalSize as number,
                    compressedSize as number,
                  ),
                };
              });
            },
          })
            .then((response) => {
              message.success(t('upload.fileUploadedSuccessfully', { fileName: file.name }));
              console.log('File uploaded successfully, invalidating queries.');
              // Add small delay to ensure backend has processed the file
              setTimeout(() => {
                // Invalidate file-related queries to refresh the file list
                queryClient.invalidateQueries({ queryKey: ['useImages', myday.id] });
                queryClient.invalidateQueries({ queryKey: ['useFilesOnly', myday.id] });
                queryClient.invalidateQueries({ queryKey: ['useAllFiles', myday.id] });
                queryClient.invalidateQueries({ queryKey: ['useMydays'] });
              }, 500);
              return response.data;
            })
            .catch((err) => {
              console.error('Create File error: ', err.response?.data?.message, err.response);
              message.error(
                t('upload.uploadFailed', { error: err.response?.data?.message || t('upload.unknownError') }),
              );
            });
        }
      }
    } catch (error) {
      console.error('File processing error:', error);
      message.error(t('upload.failedToProcessFile'));
    }
  };

  return (
    <Space direction="vertical" className={styles.container}>
      <h3>{t('upload.uploadImagesOrFiles')}</h3>
      <Upload.Dragger
        multiple
        showUploadList={false}
        listType="picture"
        customRequest={(options) => handleUpload({ file: options.file as File })}
        beforeUpload={(file) => {
          console.log('File: ', file);
          if (file.size > 2_000_000_000) {
            message.error(t('upload.fileTooLarge'));
            return false; // return true hvis filen skal legges Title, false hvis den feks er for stor
          }
        }}
      >
        {t('upload.dragFilesHere')} <br /> <Button>{t('upload.clickToUpload')}</Button>{' '}
      </Upload.Dragger>
      {Object.values(fileList)?.map((file) => {
        return (
          <Space direction="vertical" className={styles.fileProgressContainer} key={file.uid}>
            <Space>
              <FileOutlined />
              <Typography>
                {file?.name}, {getFileSize(file?.size)}MB
                {file?.isCompressed && (
                  <Typography.Text type="success" className={styles.compressedText}>
                    (compressed from {getFileSize(file?.originalSize)}MB)
                  </Typography.Text>
                )}
              </Typography>
              <Typography.Text type="secondary">
                {' '}
                {t('upload.isBeingUploadedAt', { speed: formatSpeed(file?.speed) })} - {t('upload.timeRemainingIs')}{' '}
                {formatTimeDuration(file?.estimated, t)}
              </Typography.Text>
            </Space>
            <Progress
              percent={file.progress}
              format={(percent) => `${percent}%`}
              status={file.progress === 100 ? 'success' : 'active'}
            />
            {file.speed > 0 && (
              <Typography.Text type="secondary" className={styles.speedText}>
                {t('upload.speed')}: {formatSpeed(file.speed)} | {t('upload.progress')}:{' '}
                {Math.round(((file.progress || 0) * file.size) / 100 / (1024 * 1024))}MB /{' '}
                {Math.round(file.size / (1024 * 1024))}MB
              </Typography.Text>
            )}
          </Space>
        );
      })}
      <Typography>{myday.file_count > 0 && <h3>{t('upload.existingFiles')}:</h3>}</Typography>
      <Images myday={myday} compact isEdit />
      <Files myday={myday} compact isEdit />
    </Space>
  );
};

export default UploadFilesDragger;
